// ============================================================
//  TRANSACTIONS PAGE — ADMIN DASHBOARD
//  Path: src/pages/TransactionsPage.tsx
//
//  Every payment attempt ever made — succeeded or failed — not
//  just the final outcome. This is what lets you actually see
//  "card declined twice, then succeeded on the 3rd attempt"
//  instead of only the task's final paymentStatus.
//
//  Access: super_admin, admin only.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listPaymentTransactions, retryFailedRefund, refundExpertSubscription } from '../services/admin.service'
import { PaymentTransactionRecord } from '../types'

// FIX (review pass):
//  - worker_payout / expert_payout (in the schema and types.ts) had no
//    label, so they showed as raw snake_case and couldn't be picked in
//    the Type filter.
//  - A failed load showed "No transactions found" / "0 total attempts".
//  - Only 'succeeded' was green and EVERYTHING else red; unknown
//    statuses are now neutral.
//  - Failed rows show the retry count the backend already tracks
//    (attemptCount / lastAttemptAt), and the full error on hover.
//  - Switching filters quickly can't show a superseded request's data.

// Sent by the backend (Prisma include returns every column) but not on
// the shared type yet.
type TransactionRow = PaymentTransactionRecord & {
  attemptCount?: number
  lastAttemptAt?: string | null
  // task now also carries the fee breakdown fields (see admin.service.js's
  // listPaymentTransactions) so a hire_charge row can be expanded into
  // its full itemization without a second request.
  task?: any
}

const TYPE_LABELS: Record<string, string> = {
  hire_charge:           'Hire Charge',
  refund:                'Refund',
  instant_payout:        'Instant Payout (legacy)',
  worker_payout:         'Worker Payment',
  // Written when the held card payment is CAPTURED: the money went into
  // the tasker's own Stripe account (direct charge), minus the platform fee.
  expert_payout:         'Tasker Payment (captured)',
  background_check_fee:  'Background Check Fee',
  dispute:               'Dispute / Chargeback',
  transfer_reversal:     'Transfer Reversed',
  payout_failure:        'Payout Failed',
  subscription_fee:      'Subscription Fee',
}

const statusClass = (status: string) =>
  status === 'succeeded' ? 'bg-green-600/20 text-green-400'
    : status === 'failed' ? 'bg-red-600/20 text-red-400'
    : status === 'authorized' ? 'bg-blue-600/20 text-blue-400'
    : 'bg-gray-600/20 text-gray-400'

// 'authorized' = a hire_charge that is only HELD on the customer's card; it
// turns into 'succeeded' when the job is confirmed and the card is charged.
const STATUS_LABELS: Record<string, string> = {
  authorized: 'on hold',
  retried:    'retried',
}
const statusLabel = (status: string) => STATUS_LABELS[status] || status

// These three previously only existed as log lines — nothing in
// the dashboard ever surfaced them. Highlighting them distinctly
// here is the whole point of this fix: an admin scanning this
// page should immediately spot a dispute or reversed transfer
// instead of it blending in with routine hire_charge activity.
const NEEDS_ATTENTION_TYPES = ['dispute', 'transfer_reversal', 'payout_failure']

const TransactionsPage: React.FC = () => {
  const navigate = useNavigate()
  const [transactions, setTransactions] = useState<TransactionRow[]>([])
  const [total, setTotal]               = useState(0)
  const [page, setPage]                 = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter]     = useState('')
  const [loading, setLoading]           = useState(true)
  const [retryingId, setRetryingId]     = useState<string | null>(null)
  const [retryError, setRetryError]     = useState<string | null>(null)
  const [loadError, setLoadError]       = useState('')
  const [expandedId, setExpandedId]     = useState<string | null>(null)
  const requestSeq = useRef(0)

  const fetchTransactions = useCallback(async () => {
    const seq = ++requestSeq.current
    setLoading(true)
    setLoadError('')
    try {
      const res = await listPaymentTransactions({
        page, limit: 30,
        status: statusFilter || undefined,
        type:   typeFilter || undefined,
      })
      if (seq !== requestSeq.current) return
      setTransactions(res.data.data)
      setTotal(res.data.pagination.total)
    } catch (err: any) {
      if (seq !== requestSeq.current) return
      console.error('Failed to fetch transactions:', (err as any)?.response?.status, (err as any)?.message)
      setTransactions([])
      setTotal(0)
      setLoadError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load transactions.'))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [page, statusFilter, typeFilter])

  useEffect(() => { fetchTransactions() }, [fetchTransactions])

  const handleRetryRefund = async (transactionId: string) => {
    setRetryingId(transactionId)
    setRetryError(null)
    try {
      await retryFailedRefund(transactionId)
      await fetchTransactions()
    } catch (err: any) {
      setRetryError(err.response?.data?.message || 'Retry failed — check Stripe Dashboard for details')
    } finally {
      setRetryingId(null)
    }
  }

  const handleRefundSubscription = async (transactionId: string) => {
    if (!window.confirm('Refund this subscription charge? This reverses money already collected.')) return
    setRetryingId(transactionId)
    setRetryError(null)
    try {
      await refundExpertSubscription(transactionId)
      await fetchTransactions()
    } catch (err: any) {
      setRetryError(err.response?.data?.message || 'Refund failed — check Stripe Dashboard for details')
    } finally {
      setRetryingId(null)
    }
  }

  const totalPages = Math.ceil(total / 30)
  const failedCount = transactions.filter((t) => t.status === 'failed').length

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Transaction History</h1>
          <p className="text-gray-400 text-sm mt-1">{loadError ? '—' : `${total} total attempts logged`}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex gap-2">
          {['', 'succeeded', 'authorized', 'failed'].map((s) => (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1) }}
              className={`px-4 py-2 text-sm rounded-xl transition-colors ${
                statusFilter === s ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-gray-400 hover:text-white'
              }`}>{s ? statusLabel(s) : 'All'}</button>
          ))}
        </div>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
          className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none">
          <option value="">All Types</option>
          {Object.entries(TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {(() => {
        const needsAttentionCount = transactions.filter((t) => NEEDS_ATTENTION_TYPES.includes(t.type)).length
        if (needsAttentionCount === 0) return null
        return (
          <div className="bg-orange-500/5 border border-orange-500/20 rounded-2xl p-4 mb-6">
            <p className="text-sm text-orange-400">
              ⚠️ {needsAttentionCount} item{needsAttentionCount === 1 ? '' : 's'} on this page — disputes, reversed transfers, or failed payouts — need manual review. These aren't just failed attempts; they typically require action outside the app (Stripe Dashboard, contacting the expert or customer).
            </p>
          </div>
        )
      })()}

      {loadError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-6 flex items-center justify-between gap-3">
          <p className="text-sm text-red-400">{loadError}</p>
          <button onClick={() => fetchTransactions()}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      )}

      {retryError && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 mb-6 flex items-center justify-between">
          <p className="text-sm text-red-400">{retryError}</p>
          <button onClick={() => setRetryError(null)} className="text-red-400 hover:text-white text-sm">✕</button>
        </div>
      )}

      {statusFilter === 'failed' && failedCount > 0 && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 mb-6">
          <p className="text-sm text-red-400">{failedCount} failed attempt(s) on this page — click into a task's own detail panel to see its full retry sequence in order.</p>
        </div>
      )}

      <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-light">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Type</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Task</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Customer</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Expert</th>
              {/* FIX: Amount alone was whatever THIS specific transaction
                  record moved — the full charge for a hire_charge row, but
                  only the tasker's net cut for an expert_payout/worker_payout
                  row. Scanning a payout row showed a number with no visible
                  original price or customer-paid figure to compare it
                  against. These three columns read straight off the task
                  (same fields the expandable breakdown below already uses),
                  so they're the same on every row for that task regardless
                  of which specific transaction this row is. */}
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Orig. Price</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Customer Total</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Expert Earned</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Amount (this txn)</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Error</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">When</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="text-center py-8 text-gray-400">Loading...</td></tr>
            ) : loadError ? (
              <tr><td colSpan={12} className="text-center py-8 text-gray-500">—</td></tr>
            ) : transactions.length === 0 ? (
              <tr><td colSpan={12} className="text-center py-8 text-gray-400">No transactions found</td></tr>
            ) : (
              transactions.map((t) => {
                const needsAttention = NEEDS_ATTENTION_TYPES.includes(t.type)
                // FIX: this used to gate on t.type === 'hire_charge' only —
                // a payout/refund row for the same task couldn't be expanded
                // at all, so seeing the full itemized breakdown meant going
                // to find that task's hire_charge row instead. The three
                // summary columns above (Orig. Price / Customer Paid /
                // Expert Earned) cover the quick-glance numbers; this
                // expand is for the itemized fee-by-fee detail, and any row
                // with a task attached can show it now, not just hire_charge.
                const canExpand = !!t.task
                const isExpanded = expandedId === t.id
                return (
                <React.Fragment key={t.id}>
                <tr className={`border-b border-border-light hover:bg-surface-tertiary/50 transition-colors ${
                  needsAttention ? 'border-l-2 border-l-orange-500' : ''
                } ${canExpand ? 'cursor-pointer' : ''}`}
                  onClick={canExpand ? () => setExpandedId(isExpanded ? null : t.id) : undefined}>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${needsAttention ? 'bg-orange-600/20 text-orange-400' : 'bg-brand-600/20 text-brand-400'}`}>
                      {needsAttention && '⚠️ '}{TYPE_LABELS[t.type] || t.type}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">
                    {t.task?.title || '—'}{canExpand && <span className="text-gray-600 ml-1">{isExpanded ? '▲' : '▼'}</span>}
                  </td>
                  {/* Customer/expert names link to their profile —
                      stopPropagation so clicking the name doesn't also
                      trigger the row's own expand/collapse. Customer
                      links to /customers/:id (User.id); expert links to
                      /experts/:id (Expert.id) — see the id fields' notes
                      in types/index.ts's PaymentTransactionRecord. */}
                  <td className="px-5 py-4 text-sm">
                    {t.task?.customer?.user ? (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/customers/${t.task!.customer!.user.id}`) }}
                        className="text-gray-300 hover:text-brand-400 hover:underline transition-colors text-left">
                        {t.task.customer.user.firstName} {t.task.customer.user.lastName}
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    {t.expert ? (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/experts/${t.expert!.id}`) }}
                        className="text-gray-300 hover:text-brand-400 hover:underline transition-colors text-left">
                        {t.expert.user.firstName} {t.expert.user.lastName}
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  {/* Read straight off the task, so these are the same on
                      every row tied to that task regardless of which
                      specific transaction this row is — not a per-row
                      recomputation, and not limited to hire_charge rows the
                      way the expandable full breakdown below still is. */}
                  <td className="px-5 py-4 text-sm text-gray-300">{t.task?.finalPrice != null ? `$${t.task.finalPrice.toFixed(2)}` : '—'}</td>
                  <td className="px-5 py-4 text-sm text-gray-300">{t.task?.totalCharged != null ? `$${t.task.totalCharged.toFixed(2)}` : '—'}</td>
                  <td className="px-5 py-4 text-sm text-green-400">{t.task?.taskerPayout != null ? `$${t.task.taskerPayout.toFixed(2)}` : '—'}</td>
                  <td className="px-5 py-4 text-sm text-gray-300">${t.amount.toFixed(2)} {t.currency.toUpperCase()}</td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${statusClass(t.status)}`}>
                      {statusLabel(t.status)}
                    </span>
                    {t.status === 'failed' && (t.attemptCount ?? 0) > 1 && (
                      <p className="text-xs text-gray-500 mt-1" title={t.lastAttemptAt ? `Last tried ${new Date(t.lastAttemptAt).toLocaleString()}` : undefined}>
                        {t.attemptCount} attempts
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-xs text-red-400 max-w-xs truncate" title={t.errorMessage || undefined}>{t.errorMessage || '—'}</td>
                  <td className="px-5 py-4 text-sm text-gray-400">{new Date(t.createdAt).toLocaleString()}</td>
                  <td className="px-5 py-4">
                    {t.type === 'refund' && t.status === 'failed' ? (
                      <button onClick={(e) => { e.stopPropagation(); handleRetryRefund(t.id) }} disabled={retryingId === t.id}
                        className="px-3 py-1.5 text-xs bg-yellow-500/10 text-yellow-400 rounded-lg hover:bg-yellow-500/20 disabled:opacity-50 transition-colors">
                        {retryingId === t.id ? 'Retrying...' : 'Retry Refund'}
                      </button>
                    ) : t.type === 'subscription_fee' && t.status === 'succeeded' ? (
                      <button onClick={(e) => { e.stopPropagation(); handleRefundSubscription(t.id) }} disabled={retryingId === t.id}
                        className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 disabled:opacity-50 transition-colors">
                        {retryingId === t.id ? 'Refunding...' : 'Refund'}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-600">—</span>
                    )}
                  </td>
                </tr>
                {isExpanded && t.task && (
                  <tr className="border-b border-border-light bg-surface-tertiary/30">
                    <td colSpan={12} className="px-5 py-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-xs">
                        <div><p className="text-gray-500">Base Price</p><p className="text-gray-200">{t.task.finalPrice != null ? `$${t.task.finalPrice.toFixed(2)}` : '—'}</p></div>
                        <div><p className="text-gray-500">Insurance (C/T)</p><p className="text-gray-200">${(t.task.insuranceFee ?? 0).toFixed(2)} / ${(t.task.insuranceFeeTasker ?? 0).toFixed(2)}</p></div>
                        <div><p className="text-gray-500">Service Fee (C/T)</p><p className="text-gray-200">${(t.task.serviceFee ?? 0).toFixed(2)} / ${(t.task.serviceFeeTasker ?? 0).toFixed(2)}</p></div>
                        <div><p className="text-gray-500">Processing Fee (C/T)</p><p className="text-gray-200">${(t.task.processingFee ?? 0).toFixed(2)} / ${(t.task.processingFeeTasker ?? 0).toFixed(2)}</p></div>
                        <div><p className="text-gray-500">Tax (customer / on fees)</p><p className="text-gray-200">${(t.task.taxAmount ?? 0).toFixed(2)} / ${(t.task.taxFeeTasker ?? 0).toFixed(2)}</p></div>
                        <div><p className="text-gray-500">Total Charged</p><p className="text-gray-200 font-medium">{t.task.totalCharged != null ? `$${t.task.totalCharged.toFixed(2)}` : '—'}</p></div>
                        {(() => {
                          const isFinal = !!t.task.stripeFeeRecordedAt && t.task.taskerNetFinal != null && t.task.stripeFeeActual != null
                          const pf = (t.task.processingFee ?? 0) + (t.task.processingFeeTasker ?? 0)
                          const stripe = isFinal ? t.task.stripeFeeActual : pf
                          const net = isFinal ? t.task.taskerNetFinal : t.task.taskerPayout
                          const app = t.task.totalCharged != null && t.task.taskerPayout != null ? t.task.totalCharged - t.task.taskerPayout - pf : null
                          return (
                            <>
                              <div><p className="text-gray-500">Stripe Took{isFinal ? '' : ' (est.)'}</p><p className="text-blue-400 font-medium">${(stripe as number).toFixed(2)}</p></div>
                              <div><p className="text-gray-500">Tasker Payout (Net{isFinal ? ', final' : ', est.'})</p><p className="text-green-400 font-medium">{net != null ? `$${(net as number).toFixed(2)}` : '—'}</p></div>
                              <div><p className="text-gray-500">Platform Took</p><p className="text-brand-400 font-medium">{app != null ? `$${app.toFixed(2)}` : '—'}</p></div>
                              {(() => {
                                // Difference between the processing fee charged and Stripe's real fee.
                                const kind = (t.task as any).feeSettlementKind as string | null | undefined
                                const amt = Number((t.task as any).feeSettlementAmount) || 0
                                if (kind === 'refunded' && amt > 0) {
                                  return <div><p className="text-gray-500">Processing fee difference</p><p className="text-yellow-400 font-medium">${amt.toFixed(2)} refunded to customer</p></div>
                                }
                                if (kind === 'covered' && amt > 0) {
                                  return <div><p className="text-gray-500">Processing fee difference</p><p className="text-yellow-400 font-medium">${amt.toFixed(2)} covered by MatchNBook</p></div>
                                }
                                if (kind === 'skipped') {
                                  return <div><p className="text-gray-500">Processing fee difference</p><p className="text-gray-400 font-medium">Not settled (partial or refunded charge)</p></div>
                                }
                                return null
                              })()}
                            </>
                          )
                        })()}
                      </div>
                      <p className="text-xs text-gray-600 mt-2">C/T = charged to Customer / deducted from Tasker.</p>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border-light">
            <p className="text-sm text-gray-400">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Previous</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TransactionsPage